import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Contact, CountryMetadata, FatcaChoice } from '@shared/models/data-model';
import { CountryService, MAIN_MARKETS_COUNTRY_ISO } from '@shared/services/country.service';
import { RegistrationService } from '@shared/services/registration.service';
import { NGXLogger } from 'ngx-logger';
import { PersonFormGroup, PersonFormHelperService } from './person-form-helper.service';

@Component({
    selector: 'person-form',
    templateUrl: './person-form.component.html',
    styleUrl: './person-form.component.scss'
})
export class PersonFormComponent implements OnChanges {
    @Input()
    personFormGroup: FormGroup<PersonFormGroup> = null;

    @Input()
    readonly: boolean = false;

    @Input()
    emailIsReadonly: boolean = false;

    @Input()
    applyUniquenessChecks: boolean = false;

    @Input()
    checkInArray: Contact[];

    @Input()
    translatedCountries: CountryMetadata[] = [];

    @ViewChild('mobileNumberInputElement')
    mobileNumberInputElement: ElementRef<HTMLInputElement>;

    countryOfIncorporation: string;
    completeCountryList: CountryMetadata[];
    loading = true;

    get FatcaChoice() {
        return FatcaChoice;
    }

    constructor(
        private readonly countryService: CountryService,
        private readonly registrationService: RegistrationService,
        private readonly personFormHelperService: PersonFormHelperService,
        private readonly logger: NGXLogger
    ) {
        this.countryOfIncorporation = this.registrationService.getCountryOfIncorporation();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.translatedCountries && this.translatedCountries?.length) {
            const preferredCountries = this.translatedCountries.filter(c => MAIN_MARKETS_COUNTRY_ISO.includes(c.iso3));
            preferredCountries.sort(this.countryService.sortFunction);

            const otherCountries = this.translatedCountries.filter(c => c.icc && !MAIN_MARKETS_COUNTRY_ISO.includes(c.iso3));
            otherCountries.sort(this.countryService.sortFunction);

            this.completeCountryList = preferredCountries.concat(otherCountries);
            this.loading = false;
        }

        if (!this.loading) {
            this.setDefaultValidatorsAndBehaviors();
        }
    }

    getNationalityLabel(): string {
        const nationality = this.personFormGroup.value.nationality;
        return this.completeCountryList?.find(c => c.iso3 === nationality)?.name ?? '';
    }

    getCountryOfResidenceLabel(): string {
        const countryOfResidence = this.personFormGroup.value.address?.countryISO;
        return this.completeCountryList?.find(c => c.iso3 === countryOfResidence)?.name ?? '';
    }

    getMobileNumberLabel(): string {
        const { prefix, number } = this.personFormGroup.controls.mobile.value;
        // Join only the parts that are actually present, so a missing prefix/number never renders
        // as the literal string "null"/"undefined".
        return [prefix, number].filter(Boolean).join('');
    }

    setDefaultValidatorsAndBehaviors(): void {
        const personControls = this.personFormGroup.controls;

        if (this.readonly) {
            for (const control of Object.values(personControls)) {
                control.setValidators([]);
            }
        } else {

            this.personFormHelperService.applyDefaultValidators(this.personFormGroup);
            if (this.applyUniquenessChecks) {
                this.personFormHelperService.applyUniquenessValidators(this.personFormGroup, this.checkInArray);
            }

            this.setupMobileNumberTypingAid();

            const recalculateConditionalControls = () => {
                this.logger.debug("COUNTRY/NATIONALITY CHANGED: -> recalculating date of birth, codice fiscale, and fatca question");
                this.personFormHelperService.addOrRemoveDateOfBirthAndCodiceFiscale(this.personFormGroup, this.countryOfIncorporation);
                this.personFormHelperService.addOrRemoveFatcaSubjectQuestion(this.personFormGroup);
                this.logger.debug("COUNTRY/NATIONALITY CHANGED: -> adding default validators");
                this.personFormHelperService.applyDefaultValidators(this.personFormGroup);
                if (this.applyUniquenessChecks) {
                    this.personFormHelperService.applyUniquenessValidators(this.personFormGroup, this.checkInArray);
                }
            };

            // FATCA visibility depends on BOTH residence country and nationality, so recalculate on
            // either. Residence uses statusChanges (its validity changes drive the address subform);
            // nationality uses valueChanges because switching between two valid countries doesn't
            // change validity and would never emit statusChanges.
            personControls.address.controls.countryISO.statusChanges.subscribe(recalculateConditionalControls);
            personControls.nationality.valueChanges.subscribe(recalculateConditionalControls);
        }
    }

    setupMobileNumberTypingAid(): void {
        const numberField = this.personFormGroup.controls.mobile.controls.number;
        const prefixField = this.personFormGroup.controls.mobile.controls.prefix;

        if (!this.personFormGroup.value.mobile.prefix) {
            const suggestedPrefix = this.completeCountryList?.find(c => c.iso3 === this.countryOfIncorporation)?.icc;
            prefixField.setValue(suggestedPrefix);
        }

        numberField.valueChanges.subscribe((value: string) => {

            switch (prefixField.value) {
                case "+44":
                case "+33":
                    if (value?.startsWith("0")) {
                        const cursorPosition = this.mobileNumberInputElement.nativeElement.selectionStart;
                        numberField.setValue(value.substring(1), {
                            emitEvent: false
                        });
                        this.mobileNumberInputElement.nativeElement.setSelectionRange(cursorPosition, cursorPosition);
                    }
                    break;
            }

        });

    }
}
